import {
  ComplianceForm,
  DocumentStatus,
  ClientProfile,
  DemoScenario,
  TransactionThreshold,
  TransactionSummary
} from './types';

// Hardcoded Swiss compliance forms data
export const swissComplianceForms: ComplianceForm[] = [
  // KYC/AML Forms
  {
    id: 'kyc-001',
    name: 'Client Identification Form (Form A)',
    description: 'Basic KYC form required for all new clients under Swiss AML regulations',
    category: 'kyc',
    regulation: 'AML',
    required: true,
    frequency: 'periodic',
    validityPeriod: 365, // 1 year
    pdfUrl: '/compliance-forms/kyc-form-a.pdf',
    swissSpecific: true,
    triggerConditions: [
      { type: 'time_based', operator: 'expired', value: 365, unit: 'days' }
    ],
    requiredFields: [
      { id: 'full_name', label: 'Full Name', type: 'text', required: true },
      { id: 'date_of_birth', label: 'Date of Birth', type: 'date', required: true },
      { id: 'nationality', label: 'Nationality', type: 'select', required: true },
      { id: 'residence', label: 'Country of Residence', type: 'select', required: true },
      { id: 'tax_id', label: 'Tax Identification Number', type: 'text', required: true }
    ]
  },
  {
    id: 'kyc-002',
    name: 'Beneficial Ownership Declaration',
    description: 'Declaration of beneficial ownership required for complex structures',
    category: 'kyc',
    regulation: 'AML',
    required: true,
    frequency: 'annual',
    validityPeriod: 365,
    pdfUrl: '/compliance-forms/beneficial-ownership.pdf',
    swissSpecific: true,
    triggerConditions: [
      { type: 'client_type', operator: 'in', value: ['institutional', 'professional'] }
    ]
  },

  // FinSA Suitability Forms
  {
    id: 'finsa-001',
    name: 'Client Classification Form',
    description: 'FinSA mandated client classification (retail/professional/institutional)',
    category: 'suitability',
    regulation: 'FinSA',
    required: true,
    frequency: 'once',
    pdfUrl: '/compliance-forms/finsa-classification.pdf',
    swissSpecific: true,
    requiredFields: [
      { id: 'client_type', label: 'Client Type', type: 'select', required: true,
        options: ['Private Client', 'Professional Client', 'Institutional Client'] },
      { id: 'experience', label: 'Investment Experience (years)', type: 'number', required: true },
      { id: 'knowledge', label: 'Financial Knowledge Level', type: 'select', required: true,
        options: ['Basic', 'Intermediate', 'Advanced', 'Expert'] }
    ]
  },
  {
    id: 'finsa-002',
    name: 'Suitability Assessment',
    description: 'Assessment of investment suitability based on client profile and objectives',
    category: 'suitability',
    regulation: 'FinSA',
    required: true,
    frequency: 'annual',
    validityPeriod: 365,
    pdfUrl: '/compliance-forms/suitability-assessment.pdf',
    swissSpecific: true,
    triggerConditions: [
      { type: 'transaction_amount', operator: '>', value: TransactionThreshold.Small }
    ]
  },
  {
    id: 'finsa-003',
    name: 'Risk Disclosure Document',
    description: 'Comprehensive risk disclosure for complex financial instruments',
    category: 'regulatory',
    regulation: 'FinSA',
    required: false,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/risk-disclosure.pdf',
    swissSpecific: true,
    triggerConditions: [
      { type: 'asset_type', operator: 'contains', value: ['derivatives', 'structured_products'] }
    ]
  },

  // MiFID II Requirements (adopted in Switzerland)
  {
    id: 'mifid-001',
    name: 'Cost Transparency Disclosure',
    description: 'Ex-ante and ex-post cost disclosure as per MiFID II',
    category: 'regulatory',
    regulation: 'MiFID II',
    required: true,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/cost-disclosure.pdf',
    triggerConditions: [
      { type: 'transaction_amount', operator: '>', value: 0 }
    ]
  },
  {
    id: 'mifid-002',
    name: 'ESG Preferences Assessment',
    description: 'Environmental, Social, and Governance preferences documentation',
    category: 'suitability',
    regulation: 'MiFID II',
    required: false,
    frequency: 'annual',
    validityPeriod: 365,
    pdfUrl: '/compliance-forms/esg-preferences.pdf',
    triggerConditions: [
      { type: 'client_type', operator: 'in', value: ['retail', 'professional'] }
    ]
  },
  {
    id: 'mifid-003',
    name: 'Best Execution Policy',
    description: 'Acknowledgment of best execution policy',
    category: 'trading',
    regulation: 'MiFID II',
    required: true,
    frequency: 'once',
    pdfUrl: '/compliance-forms/best-execution.pdf'
  },

  // FATCA/CRS Tax Forms
  {
    id: 'fatca-001',
    name: 'W-8BEN Form',
    description: 'Certificate of Foreign Status for US tax purposes',
    category: 'tax',
    regulation: 'FATCA',
    required: false,
    frequency: 'periodic',
    validityPeriod: 1095, // 3 years
    pdfUrl: '/compliance-forms/w8ben.pdf',
    triggerConditions: [
      { type: 'jurisdiction', operator: 'contains', value: 'US' }
    ]
  },
  {
    id: 'crs-001',
    name: 'CRS Self-Certification',
    description: 'Common Reporting Standard self-certification for tax residency',
    category: 'tax',
    regulation: 'CRS',
    required: true,
    frequency: 'once',
    pdfUrl: '/compliance-forms/crs-certification.pdf',
    requiredFields: [
      { id: 'tax_residency', label: 'Country of Tax Residency', type: 'select', required: true },
      { id: 'tax_number', label: 'Tax Identification Number', type: 'text', required: true }
    ]
  },

  // PRIIPs (Packaged Retail Investment Products)
  {
    id: 'priips-001',
    name: 'Key Information Document (KID)',
    description: 'PRIIPs KID for packaged investment products',
    category: 'regulatory',
    regulation: 'PRIIPs',
    required: false,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/priips-kid.pdf',
    triggerConditions: [
      { type: 'asset_type', operator: 'contains', value: ['structured_products', 'funds'] }
    ]
  },

  // Transaction-specific forms
  {
    id: 'buy-001',
    name: 'Purchase Order Authorization',
    description: 'Securities purchase order authorization form',
    category: 'trading',
    regulation: 'MiFID II',
    required: true,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/purchase-order.pdf',
    swissSpecific: false,
    requiredFields: [
      { id: 'security_name', label: 'Security Name', type: 'text', required: true },
      { id: 'isin', label: 'ISIN', type: 'text', required: true },
      { id: 'quantity', label: 'Quantity', type: 'number', required: true },
      { id: 'price', label: 'Price per Unit', type: 'number', required: true }
    ]
  },
  {
    id: 'sell-001',
    name: 'Disposal Order Authorization',
    description: 'Securities disposal/sale order authorization form',
    category: 'trading',
    regulation: 'MiFID II',
    required: true,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/disposal-order.pdf',
    swissSpecific: false,
    requiredFields: [
      { id: 'security_name', label: 'Security Name', type: 'text', required: true },
      { id: 'isin', label: 'ISIN', type: 'text', required: true },
      { id: 'quantity', label: 'Quantity to Sell', type: 'number', required: true },
      { id: 'price', label: 'Expected Price', type: 'number', required: true }
    ]
  },
  {
    id: 'cost-buy',
    name: 'Cost Disclosure - Buy Transaction',
    description: 'Ex-ante cost disclosure for buy transactions',
    category: 'regulatory',
    regulation: 'PRIIPs',
    required: true,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/cost-disclosure-buy.pdf',
    swissSpecific: false,
    requiredFields: [
      { id: 'transaction_costs', label: 'Transaction Costs', type: 'number', required: true },
      { id: 'ongoing_costs', label: 'Ongoing Costs', type: 'number', required: true },
      { id: 'incidental_costs', label: 'Incidental Costs', type: 'number', required: false }
    ]
  },
  {
    id: 'cost-sell',
    name: 'Cost Disclosure - Sell Transaction',
    description: 'Ex-ante cost disclosure for sell transactions',
    category: 'regulatory',
    regulation: 'PRIIPs',
    required: true,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/cost-disclosure-sell.pdf',
    swissSpecific: false,
    requiredFields: [
      { id: 'transaction_costs', label: 'Transaction Costs', type: 'number', required: true },
      { id: 'exit_costs', label: 'Exit Costs', type: 'number', required: true },
      { id: 'tax_implications', label: 'Tax Implications', type: 'text', required: false }
    ]
  },
  {
    id: 'rebal-001',
    name: 'Portfolio Rebalancing Authorization',
    description: 'Client approval for portfolio rebalancing strategy',
    category: 'suitability',
    regulation: 'FinSA',
    required: true,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/rebalancing-auth.pdf',
    swissSpecific: true,
    requiredFields: [
      { id: 'current_allocation', label: 'Current Allocation', type: 'text', required: true },
      { id: 'proposed_allocation', label: 'Proposed Allocation', type: 'text', required: true },
      { id: 'rebalancing_reason', label: 'Reason for Rebalancing', type: 'text', required: true }
    ]
  },
  {
    id: 'sof-001',
    name: 'Source of Funds Declaration',
    description: 'Documentation of cash/deposit origin for AML compliance',
    category: 'kyc',
    regulation: 'AML',
    required: true,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/source-of-funds.pdf',
    swissSpecific: true,
    requiredFields: [
      { id: 'source_description', label: 'Source Description', type: 'text', required: true },
      { id: 'amount', label: 'Amount', type: 'number', required: true },
      { id: 'currency', label: 'Currency', type: 'select', required: true },
      { id: 'supporting_documents', label: 'Supporting Documents', type: 'file', required: false }
    ]
  },
  {
    id: 'complex-001',
    name: 'Complex Products Risk Disclosure',
    description: 'Enhanced disclosure for derivatives/structured products',
    category: 'regulatory',
    regulation: 'FinSA',
    required: true,
    frequency: 'per-transaction',
    pdfUrl: '/compliance-forms/complex-products.pdf',
    swissSpecific: true,
    requiredFields: [
      { id: 'product_type', label: 'Product Type', type: 'select', required: true },
      { id: 'risk_level', label: 'Risk Level', type: 'select', required: true },
      { id: 'client_acknowledgment', label: 'Client Acknowledgment', type: 'checkbox', required: true }
    ]
  }
];

// Sample document statuses for demo
export const sampleDocumentStatuses: DocumentStatus[] = [
  {
    formId: 'kyc-001',
    formName: 'Client Identification Form (Form A)',
    status: 'valid',
    completedDate: '2024-01-15',
    expiryDate: '2025-01-15',
    daysUntilExpiry: 4,
    completedBy: 'John Smith'
  },
  {
    formId: 'kyc-002',
    formName: 'Beneficial Ownership Declaration',
    status: 'expiring_soon',
    completedDate: '2024-01-10',
    expiryDate: '2025-01-10',
    daysUntilExpiry: -2,
    completedBy: 'John Smith'
  },
  {
    formId: 'finsa-001',
    formName: 'Client Classification Form',
    status: 'valid',
    completedDate: '2024-06-01',
    completedBy: 'Compliance Team'
  },
  {
    formId: 'finsa-002',
    formName: 'Suitability Assessment',
    status: 'expired',
    completedDate: '2023-12-01',
    expiryDate: '2024-12-01',
    daysUntilExpiry: -41,
    completedBy: 'Previous Advisor'
  },
  {
    formId: 'mifid-001',
    formName: 'Cost Transparency Disclosure',
    status: 'missing'
  },
  {
    formId: 'crs-001',
    formName: 'CRS Self-Certification',
    status: 'valid',
    completedDate: '2024-03-15',
    completedBy: 'Client'
  },
  {
    formId: 'fatca-001',
    formName: 'W-8BEN Form',
    status: 'not_required',
    notes: 'Client is not a US person'
  }
];

// Demo scenarios for different client types
export const demoScenarios: DemoScenario[] = [
  {
    id: 'scenario-1',
    name: 'Retail Client - Small Transaction',
    description: 'Private client with portfolio < 100k CHF',
    clientProfile: {
      id: 'client-001',
      name: 'Anna Müller',
      type: 'retail',
      classification: 'non-qualified',
      riskProfile: 'conservative',
      nationality: 'CH',
      residency: 'CH',
      taxStatus: 'swiss_resident',
      investorType: 'retail',
      kycStatus: 'valid',
      kycExpiryDate: '2025-01-15',
      portfolioValue: 75000,
      hasUSIndicia: false,
      requiresESGPreferences: true
    },
    transaction: {
      type: 'buy',
      totalValue: 25000,
      numberOfTransactions: 3,
      assetTypes: ['equities', 'bonds'],
      markets: ['SIX', 'Euronext'],
      requiresApproval: false,
      complexProducts: false
    },
    expectedCompliance: {
      requiredForms: ['kyc-001', 'finsa-001', 'finsa-002', 'mifid-001', 'crs-001'],
      warnings: ['Suitability assessment expires in 30 days'],
      estimatedTime: '45 minutes'
    }
  },
  {
    id: 'scenario-2',
    name: 'Professional Client - Large Transaction',
    description: 'Qualified investor with portfolio > 2M CHF, complex products',
    clientProfile: {
      id: 'client-002',
      name: 'Tech Ventures AG',
      type: 'professional',
      classification: 'qualified',
      riskProfile: 'aggressive',
      nationality: 'CH',
      residency: 'CH',
      taxStatus: 'swiss_resident',
      investorType: 'professional',
      kycStatus: 'valid',
      kycExpiryDate: '2025-06-30',
      portfolioValue: 5500000,
      hasUSIndicia: false,
      requiresESGPreferences: false
    },
    transaction: {
      type: 'rebalance',
      totalValue: 1500000,
      numberOfTransactions: 15,
      assetTypes: ['equities', 'derivatives', 'structured_products'],
      markets: ['NYSE', 'NASDAQ', 'SIX'],
      requiresApproval: true,
      complexProducts: true
    },
    expectedCompliance: {
      requiredForms: ['kyc-001', 'kyc-002', 'finsa-001', 'finsa-003', 'mifid-001', 'mifid-003', 'priips-001'],
      warnings: ['Complex products require additional risk disclosure', 'Transaction requires senior approval'],
      estimatedTime: '2 hours'
    }
  },
  {
    id: 'scenario-3',
    name: 'US Person - FATCA Requirements',
    description: 'Client with US tax obligations',
    clientProfile: {
      id: 'client-003',
      name: 'John Davidson',
      type: 'retail',
      classification: 'non-qualified',
      riskProfile: 'moderate',
      nationality: 'US',
      residency: 'CH',
      taxStatus: 'us_person',
      investorType: 'retail',
      kycStatus: 'valid',
      kycExpiryDate: '2025-01-20',
      portfolioValue: 450000,
      hasUSIndicia: true,
      requiresESGPreferences: true
    },
    transaction: {
      type: 'advisory',
      totalValue: 150000,
      numberOfTransactions: 8,
      assetTypes: ['equities', 'bonds', 'funds'],
      markets: ['NYSE', 'SIX'],
      requiresApproval: false,
      complexProducts: false
    },
    expectedCompliance: {
      requiredForms: ['kyc-001', 'finsa-001', 'finsa-002', 'mifid-001', 'mifid-002', 'fatca-001', 'crs-001'],
      warnings: ['KYC expires in 8 days - urgent renewal required', 'FATCA reporting obligations apply'],
      estimatedTime: '1.5 hours'
    }
  }
];

// Helper function to get forms by regulation
export const getFormsByRegulation = (regulation: string): ComplianceForm[] => {
  return swissComplianceForms.filter(form => form.regulation === regulation);
};

// Helper function to get required forms for a transaction
export const getRequiredFormsForTransaction = (
  clientProfile: ClientProfile,
  transaction: TransactionSummary
): ComplianceForm[] => {
  return swissComplianceForms.filter(form => {
    // Always required forms
    if (form.required) return true;

    // Check trigger conditions
    if (form.triggerConditions) {
      for (const condition of form.triggerConditions) {
        switch (condition.type) {
          case 'transaction_amount':
            if (condition.operator === '>' && transaction.totalValue > condition.value) return true;
            if (condition.operator === '<' && transaction.totalValue < condition.value) return true;
            break;
          case 'client_type':
            if (condition.operator === 'in' && condition.value.includes(clientProfile.type)) return true;
            break;
          case 'asset_type':
            if (condition.operator === 'contains' &&
                transaction.assetTypes.some((asset: string) => condition.value.includes(asset))) return true;
            break;
          case 'jurisdiction':
            if (condition.operator === 'contains' && 
                (clientProfile.nationality === condition.value || clientProfile.hasUSIndicia)) return true;
            break;
        }
      }
    }

    return false;
  });
};

// Sample PDF URLs (these would be actual PDF files in production)
export const samplePDFUrls = {
  'kyc-form-a': 'https://www.finma.ch/en/~/media/finma/dokumente/dokumentencenter/myfinma/2ueberwachung/20200619-aml-ordinance-finma.pdf',
  'suitability-assessment': 'https://www.sif.admin.ch/dam/sif/en/dokumente/Fachinformationen/finsa_fidleg.pdf',
  'risk-disclosure': 'https://www.six-group.com/dam/download/sites/education/quiz/risk-disclosure.pdf'
};

// Compliance thresholds and limits
export const complianceThresholds = {
  kycExpiryWarningDays: 30,
  documentExpiryWarningDays: 60,
  highValueTransactionLimit: 1000000, // 1M CHF
  complexProductsRequireAdditionalDisclosure: true,
  usPerson_RequiresFATCA: true,
  professionalClientMinPortfolio: 500000 // 500k CHF
};

export default {
  swissComplianceForms,
  sampleDocumentStatuses,
  demoScenarios,
  getFormsByRegulation,
  getRequiredFormsForTransaction,
  samplePDFUrls,
  complianceThresholds
};